import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import {
  ChargeFinalPayment,
  GetRivhitPaymentStatus,
} from "../../utils/rivhitPaymentAPI";

import Swal from "sweetalert2";
const showPaymentAlert = (options) =>
  Swal.fire({
    ...options,
    didOpen: () => {
      const container = document.querySelector(
        ".swal2-container"
      );

      if (container) {
        container.style.zIndex = "9999";
      }
    },
  });

const AdminRivhitPaymentSection = ({
  order,
  finalAmount,
  disabled = false,
}) => {
  const [paymentInfo, setPaymentInfo] =
    useState(null);

  const [loadingStatus, setLoadingStatus] =
    useState(false);

  const [charging, setCharging] =
    useState(false);

  const [statusError, setStatusError] =
    useState("");

  const isCreditCard =
    order?.paymentMethod === "CreditCard";

  const hasJ5Authorization =
    paymentInfo?.hasJ5Authorization === true;

  const isFinalCharged =
    paymentInfo?.isFinalCharged === true;

  const paymentStatus =
    paymentInfo?.rivhitPaymentStatus ||
    "NotReceived";

  const finalChargeStatus =
    paymentInfo?.finalChargeStatus || "";

  const finalChargeReference =
    paymentInfo?.finalChargeReference || "";

  const j5Amount = Number(
    paymentInfo?.j5Amount || 0
  );

  const transactionStatus =
    paymentInfo?.transactionStatus;

  const isProcessing =
    finalChargeStatus === "Processing";

  const isFailed =
    finalChargeStatus === "Failed";

  const amountToDisplay = Number(
    finalAmount || 0
  );

  /*
   * קבלת מצב התשלום האמיתי מהשרת.
   */
  const loadPaymentStatus =
    useCallback(async () => {
      if (
        !order?.id ||
        order?.paymentMethod !==
          "CreditCard"
      ) {
        return null;
      }

      try {
        setLoadingStatus(true);
        setStatusError("");

        const result =
          await GetRivhitPaymentStatus(
            order.id
          );

        setPaymentInfo(result);

        return result;
      } catch (error) {
        console.error(
          "שגיאה בקבלת מצב התשלום:",
          error
        );

        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (typeof error?.response?.data ===
          "string"
            ? error.response.data
            : null) ||
          "לא ניתן לקבל את מצב התשלום.";

        setStatusError(message);

        return null;
      } finally {
        setLoadingStatus(false);
      }
    }, [order?.id, order?.paymentMethod]);

  /*
   * בכל פתיחת הזמנה באשראי,
   * טוענים את מצב התשלום מהשרת.
   */
  useEffect(() => {
    loadPaymentStatus();
  }, [loadPaymentStatus]);

  const handleFinalCharge = async () => {
    if (
      charging ||
      loadingStatus ||
      disabled ||
      !order?.id
    ) {
      return;
    }

    if (!hasJ5Authorization) {
      await showPaymentAlert({
        icon: "warning",
        title: "לא התקבל אישור J5",
        text:
          "לא ניתן לבצע חיוב סופי לפני שמתקבל אישור J5 תקין מריווחית.",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });

      return;
    }

    if (isFinalCharged) {
      await showPaymentAlert({
        icon: "info",
        title: "ההזמנה כבר חויבה",
        text:
          "לא ניתן לחייב את אותה הזמנה פעם נוספת.",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });

      return;
    }

    if (isProcessing) {
      await showPaymentAlert({
        icon: "info",
        title: "החיוב כבר מתבצע",
        text:
          "בקשת החיוב כבר נשלחה לריווחית וממתינה לאישור הסופי.",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });

      return;
    }

    if (
      !Number.isFinite(amountToDisplay) ||
      amountToDisplay <= 0
    ) {
      await showPaymentAlert({
        icon: "warning",
        title: "סכום ההזמנה אינו תקין",
        text:
          "לא ניתן לבצע חיוב כאשר הסכום הסופי אינו גדול מאפס.",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });

      return;
    }

    const confirmation =
      await showPaymentAlert({
        icon: "warning",
        title: "אישור חיוב סופי",
        html: `
          <div dir="rtl">
            <div>
              הסכום הסופי לחיוב:
            </div>

            <div style="
              margin-top: 10px;
              font-size: 22px;
              font-weight: bold;
            ">
              ${amountToDisplay.toFixed(2)} ₪
            </div>

            <div style="margin-top: 12px;">
              יש לוודא שכל השינויים
              בהזמנה נשמרו לפני ביצוע
              החיוב.
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText:
          "ביצוע חיוב סופי",
        cancelButtonText: "ביטול",
        confirmButtonColor: "#D81B60",
      });

    if (!confirmation.isConfirmed) {
      return;
    }

    try {
      setCharging(true);
      setStatusError("");

      /*
       * שולחים רק את מספר ההזמנה.
       * השרת משתמש במחיר האחרון
       * שנשמר במסד ובנתוני ה־J5.
       */
      const result =
        await ChargeFinalPayment(
          order.id
        );

      /*
       * טוענים שוב את הסטטוס.
       * בשלב הזה הוא עשוי להיות
       * Processing עד להגעת IPN.
       */
      await loadPaymentStatus();

      await showPaymentAlert({
        icon: "info",
        title: "בקשת החיוב נשלחה",
        text:
          result?.message ||
          "הבקשה נשלחה לריווחית וממתינה לאישור החיוב הסופי.",
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });
    } catch (error) {
      console.error(
        "שגיאה בביצוע החיוב הסופי:",
        error
      );

      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (typeof error?.response?.data ===
        "string"
          ? error.response.data
          : null) ||
        "לא ניתן היה לבצע את החיוב הסופי.";

      await loadPaymentStatus();

      await showPaymentAlert({
        icon: "error",
        title: "החיוב לא בוצע",
        text: message,
        confirmButtonText: "אישור",
        confirmButtonColor: "#D81B60",
      });
    } finally {
      setCharging(false);
    }
  };

  /*
   * לא מציגים את האזור בהזמנות
   * שאינן בכרטיס אשראי.
   */
  if (!isCreditCard) {
    return null;
  }

  return (
    <Box
      dir="rtl"
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid",
        borderColor: isFinalCharged
          ? "success.main"
          : isFailed
          ? "error.main"
          : hasJ5Authorization
          ? "info.main"
          : "warning.main",
        borderRadius: 2,
        backgroundColor: "#fff",
      }}
    >
      <Typography
        component="h3"
        variant="h6"
        sx={{
          mb: 1.5,
          fontWeight: 800,
        }}
      >
        מצב תשלום באשראי
      </Typography>
      {paymentInfo &&
  disabled &&
  !isFinalCharged && (
    <Alert
      severity="warning"
      sx={{ mb: 1.5 }}
    >
      כדי לבצע חיוב סופי, יש ללחוץ תחילה
      על הכפתור "שמור שינויים".
    </Alert>
  )}

      {loadingStatus && !paymentInfo && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <CircularProgress size={18} />

            <span>
              בודק את מצב התשלום...
            </span>
          </Box>
        </Alert>
      )}

      {statusError && (
        <Alert
          severity="error"
          sx={{ mb: 1.5 }}
        >
          {statusError}
        </Alert>
      )}

      {!loadingStatus &&
        !statusError &&
        isFinalCharged && (
          <Alert
            severity="success"
            sx={{ mb: 1.5 }}
          >
            החיוב הסופי בוצע בהצלחה.
          </Alert>
        )}

      {!loadingStatus &&
        !statusError &&
        !isFinalCharged &&
        isProcessing && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
          >
            בקשת החיוב נשלחה לריווחית.
            ממתינים לקבלת אישור החיוב
            הסופי.
          </Alert>
        )}

      {!loadingStatus &&
        !statusError &&
        !isFinalCharged &&
        !isProcessing &&
        isFailed && (
          <Alert
            severity="error"
            sx={{ mb: 1.5 }}
          >
            ניסיון החיוב הסופי נכשל.
            אפשר לבדוק את פרטי ההזמנה
            ולנסות שוב.
          </Alert>
        )}

      {!loadingStatus &&
        !statusError &&
        !isFinalCharged &&
        !isProcessing &&
        !isFailed &&
        hasJ5Authorization && (
          <Alert
            severity="info"
            sx={{ mb: 1.5 }}
          >
            התקבל אישור J5 מריווחית.
            סכום האישור:{" "}
            <strong>
              {j5Amount.toFixed(2)} ₪
            </strong>
          </Alert>
        )}

      {!loadingStatus &&
        !statusError &&
        !isFinalCharged &&
        !isProcessing &&
        !isFailed &&
        !hasJ5Authorization && (
          <Alert
            severity="warning"
            sx={{ mb: 1.5 }}
          >
            הלקוחה בחרה תשלום באשראי,
            אבל עדיין לא התקבל אישור J5
            תקין מריווחית.
          </Alert>
        )}

      {paymentInfo && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            backgroundColor: "grey.50",
            borderRadius: 1,
          }}
        >
          {/* <Typography variant="body2">
            סטטוס תשלום:{" "}
            <strong>
              {paymentStatus}
            </strong>
          </Typography>

          {transactionStatus != null && (
            <Typography variant="body2">
              קוד סטטוס העסקה:{" "}
              <strong>
                {transactionStatus}
              </strong>
            </Typography>
          )} */}

         {hasJ5Authorization && (
  <Typography variant="body2">
    סכום{" "}
    <span dir="ltr">:J5</span>{" "}
    <strong>
      {j5Amount.toFixed(2)} ₪
    </strong>
  </Typography>
)}

          {/* {finalChargeReference && (
            <Typography variant="body2">
              מזהה חיוב סופי:{" "}
              <strong>
                {finalChargeReference}
              </strong>
            </Typography>
          )} */}
        </Box>
      )}

      <Box
        sx={{
          mb: 2,
          p: 1.5,
          display: "flex",
          alignItems: "center",
         justifyContent: "flex-start",
          gap: 1,
          borderRadius: 1,
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.05rem",
          }}
        >
          מחיר סופי לחיוב:
        </Typography>

        <Typography
          sx={{
            fontWeight: 800,
            fontSize: "1.2rem",
            color: "#D81B60",
          }}
        >
          {amountToDisplay.toFixed(2)} ₪
        </Typography>
      </Box>

      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        spacing={1}
      >
        {!isFinalCharged && (
          <Button
            type="button"
            variant="contained"
            color="success"
            onClick={handleFinalCharge}
            disabled={
              disabled ||
              charging ||
              loadingStatus ||
              !hasJ5Authorization ||
              isProcessing
            }
          >
            {charging
              ? "שולח בקשת חיוב..."
              : isProcessing
              ? "ממתין לאישור החיוב"
              : hasJ5Authorization
              ? "ביצוע חיוב סופי"
              : "ממתין לאישור J5"}
          </Button>
        )}

        <Button
          type="button"
          variant="outlined"
          onClick={loadPaymentStatus}
          disabled={
            loadingStatus || charging
          }
        >
          {loadingStatus
            ? "בודק..."
            : "רענון מצב התשלום"}
        </Button>
      </Stack>
    </Box>
  );
};

export default AdminRivhitPaymentSection;